import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, useDayPicker, useNavigation } from "react-day-picker";

import { cn } from "@/app/_module/lib/utils";
import { buttonVariants } from "@/app/_module/components/ui/button";
import { Button } from "@/app/_module/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 md:p-4", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4 md:gap-6",
        month: "space-y-4 w-full",
        month_caption: "hidden", // Hide default caption
        caption_label: "text-base font-semibold",
        nav: "hidden", // Hide default nav
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-transparent p-0 hover:bg-accent"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-transparent p-0 hover:bg-accent"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex gap-2 mb-2 w-full justify-between",
        weekday:
          "text-muted-foreground flex-1 font-medium text-sm uppercase text-center",
        week: "flex gap-2 mt-1 w-full justify-between",
        day: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 flex-1",
        day_button: cn(
          "w-full aspect-square p-0 font-normal rounded-md transition-colors relative",
          "hover:bg-accent hover:text-accent-foreground",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "cursor-pointer" // Ensure cursor shows as pointer for clickable days
        ),
        range_end: "day-range-end",
        selected: "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground [&_button]:ring-2 [&_button]:ring-primary [&_button]:ring-offset-2 rounded-md",
        today: "bg-accent text-accent-foreground font-semibold ring-2 ring-primary ring-offset-2",
        outside:
          "text-muted-foreground/40 hover:bg-accent/50",
        disabled: "text-muted-foreground/30 hover:bg-transparent cursor-not-allowed",
        range_middle:
          "bg-accent text-accent-foreground rounded-none",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return <Icon className="h-4 w-4" />;
        },
        Month: ({ calendarMonth, children }) => {
          const { goToMonth, nextMonth, previousMonth } = useNavigation();
          const displayMonth = calendarMonth.date;

          return (
            <div className="space-y-4 w-full">
              {/* Custom header with month label and navigation */}
              <div className="flex justify-between items-center mb-4 w-full">
                <span className="text-base font-semibold">
                  {displayMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="h-8 w-8 bg-transparent p-0 hover:bg-accent"
                    onClick={() => previousMonth && goToMonth(previousMonth)}
                    disabled={!previousMonth}
                    type="button"
                    aria-label="Go to previous month"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="h-8 w-8 bg-transparent p-0 hover:bg-accent"
                    onClick={() => nextMonth && goToMonth(nextMonth)}
                    disabled={!nextMonth}
                    type="button"
                    aria-label="Go to next month"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {/* Calendar grid */}
              {children}
            </div>
          );
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
